import PropTypes from "prop-types";
import { useMemo } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import { useTheme, alpha } from "@mui/material/styles";
import MDTypography from "components/MDTypography";
import { DEMO_MAX_SLOT_SELECTIONS } from "./demoBookingRules";
import { formatDemoDateLabel, formatDemoSlotLabel, formatDemoTimeLabel, slotKey } from "./demoBookingRules";
import { groupSlotsByDate } from "./parseDemoBookingFromPublic";

export default function DemoSlotPicker({ availableSlots, selectedSlots, onChange }) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const datesWithTimes = useMemo(() => groupSlotsByDate(availableSlots), [availableSlots]);
  const selectedKeys = useMemo(() => new Set(selectedSlots.map(slotKey)), [selectedSlots]);
  const availableKeys = useMemo(() => new Set(availableSlots.map(slotKey)), [availableSlots]);

  const toggleSlot = (date, time) => {
    const key = `${date}_${time}`;
    if (!availableKeys.has(key)) return;
    if (selectedKeys.has(key)) {
      onChange(selectedSlots.filter((s) => slotKey(s) !== key));
      return;
    }
    const next = { date, time };
    if (selectedSlots.length >= DEMO_MAX_SLOT_SELECTIONS) {
      onChange([selectedSlots[1], next]);
      return;
    }
    onChange([...selectedSlots, next]);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {selectedSlots.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {selectedSlots.map((s) => (
            <Chip
              key={slotKey(s)}
              label={formatDemoSlotLabel(s)}
              onDelete={() => toggleSlot(s.date, s.time)}
              color="primary"
              variant="filled"
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {datesWithTimes.map(({ date, times }) => (
          <Box
            key={date}
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              p: 1.5,
              bgcolor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.04) : "background.paper",
            }}
          >
            <MDTypography variant="button" fontWeight={700} color="text.primary" sx={{ mb: 1, display: "block" }}>
              {formatDemoDateLabel(date)}
            </MDTypography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {times.map((time) => {
                const selected = selectedKeys.has(`${date}_${time}`);
                return (
                  <Button
                    key={time}
                    variant={selected ? "contained" : "outlined"}
                    onClick={() => toggleSlot(date, time)}
                    sx={{
                      flex: times.length > 1 ? "1 1 45%" : "1 1 100%",
                      fontWeight: 700,
                      borderRadius: 2,
                      textTransform: "none",
                      ...(selected
                        ? {}
                        : {
                            borderColor: theme.palette.divider,
                            color: "text.primary",
                          }),
                    }}
                  >
                    {formatDemoTimeLabel(time)}
                  </Button>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

DemoSlotPicker.propTypes = {
  availableSlots: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedSlots: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
    }),
  ),
  onChange: PropTypes.func.isRequired,
};

DemoSlotPicker.defaultProps = {
  selectedSlots: [],
};
